import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import AIPostGenerator from './AIPostGenerator';
import { Sparkles, AlertCircle } from 'lucide-react';
import { getAllPlatforms } from '../../../constants/platformLogos';
import ContentEnhancementPanel from './ContentEnhancementPanel';
import { brandService } from '../../../services/brandService';
import { socialAccountService } from '../../../services/socialAccountService';

const PostCreationForm = ({ onCancel, onSchedule }) => {
  const [formData, setFormData] = useState({
    brandId: '',
    content: '',
    socialAccountIds: [],
    media: null,
    mediaAlt: '',
    scheduledDate: new Date(),
    scheduledTime: '12:00',
    recurring: false,
    recurringPattern: 'daily'
  });

  const [brands, setBrands] = useState([]);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [showEnhancement, setShowEnhancement] = useState(false);

  const platforms = getAllPlatforms();

  const trendingHashtags = [
    '#SocialMediaMarketing', '#ContentStrategy', '#DigitalMarketing', 
    '#GrowthHacking', '#MarketingTips', '#SocialMediaTips',
    '#ContentCreation', '#BrandAwareness', '#EngagementBoost'
  ];

  // Load brands on mount
  useEffect(() => {
    loadBrands();
  }, []);

  // Load connected accounts when brand is selected
  useEffect(() => {
    if (formData?.brandId) {
      loadConnectedAccounts(formData?.brandId);
    } else {
      setConnectedAccounts([]);
      setFormData(prev => ({ ...prev, socialAccountIds: [] }));
    }
  }, [formData?.brandId]);

  const loadBrands = async () => {
    try {
      const data = await brandService?.getAll();
      setBrands(data?.filter(b => b?.isActive) || []);
    } catch (error) {
      console.error('Error loading brands:', error);
      setErrors(prev => ({ ...prev, brands: 'Failed to load brands' }));
    }
  };

  const loadConnectedAccounts = async (brandId) => {
    try {
      setLoadingAccounts(true);
      const accounts = await socialAccountService?.getByBrandId(brandId);
      setConnectedAccounts(accounts?.filter(acc => acc?.isConnected) || []);
    } catch (error) {
      console.error('Error loading connected accounts:', error);
      setErrors(prev => ({ ...prev, accounts: 'Failed to load connected accounts' }));
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleAccountToggle = (accountId) => {
    setFormData(prev => ({
      ...prev,
      socialAccountIds: prev?.socialAccountIds?.includes(accountId)
        ? prev?.socialAccountIds?.filter(id => id !== accountId)
        : [...prev?.socialAccountIds, accountId]
    }));
  };

  const handleMediaUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader?.result);
        setFormData(prev => ({ ...prev, media: reader?.result }));
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleHashtagClick = (hashtag) => {
    setFormData(prev => ({
      ...prev,
      content: prev?.content + (prev?.content ? ' ' : '') + hashtag
    }));
  };

  const handleAIPostGenerated = (generatedContent) => {
    setFormData(prev => ({ ...prev, content: generatedContent }));
    setShowAIGenerator(false);
  };

  const handleApplySuggestion = (enhancedContent) => {
    setFormData(prev => ({ ...prev, content: enhancedContent }));
  };

  const getCharacterCount = () => {
    if (formData?.socialAccountIds?.length === 0) return { current: formData?.content?.length, limit: null };
    
    const selectedAccounts = connectedAccounts?.filter(acc => formData?.socialAccountIds?.includes(acc?.id));
    const selectedPlatformIds = selectedAccounts?.map(acc => acc?.platform);
    const selectedPlatforms = platforms?.filter(p => selectedPlatformIds?.includes(p?.id));
    
    if (selectedPlatforms?.length === 0) return { current: formData?.content?.length, limit: null };
    
    const minLimit = Math.min(...selectedPlatforms?.map(p => p?.charLimit));
    
    return { current: formData?.content?.length, limit: minLimit };
  };

  const getPlatformLogo = (platformId) => {
    return platforms?.find(p => p?.id === platformId);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.brandId) {
      newErrors.brandId = 'Please select a brand';
    }
    
    if (!formData?.content?.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (formData?.socialAccountIds?.length === 0) {
      newErrors.socialAccountIds = 'Select at least one connected account';
    }
    
    const { current, limit } = getCharacterCount();
    if (limit && current > limit) {
      newErrors.content = `Content exceeds character limit for selected platforms (${limit} characters)`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    const [hours, minutes] = formData?.scheduledTime?.split(':');
    const scheduledDateTime = new Date(formData.scheduledDate);
    scheduledDateTime?.setHours(parseInt(hours), parseInt(minutes));
    
    const newPost = {
      brandId: formData?.brandId,
      content: formData?.content,
      media: formData?.media,
      mediaAlt: formData?.mediaAlt || `Social media post image`,
      scheduledDate: scheduledDateTime,
      status: 'scheduled',
      recurring: formData?.recurring,
      recurringPattern: formData?.recurringPattern
    };
    
    onSchedule(newPost, formData?.socialAccountIds);
    
    setFormData({
      brandId: '',
      content: '',
      socialAccountIds: [],
      media: null,
      mediaAlt: '',
      scheduledDate: new Date(),
      scheduledTime: '12:00',
      recurring: false,
      recurringPattern: 'daily'
    });
    setMediaPreview(null);
    setErrors({});
  };

  const { current, limit } = getCharacterCount();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Create New Post</h2>
          <Button
            onClick={onCancel}
            variant="outline"
            size="sm"
          >
            Cancel
          </Button>
        </div>

        {/* Brand Selection */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Select Brand *
          </label>
          <select
            value={formData?.brandId}
            onChange={(e) => setFormData(prev => ({ ...prev, brandId: e?.target?.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Choose a brand...</option>
            {brands?.map(brand => (
              <option key={brand?.id} value={brand?.id}>
                {brand?.name}
              </option>
            ))}
          </select>
          {errors?.brandId && (
            <p className="text-sm text-error mt-2">{errors?.brandId}</p>
          )}
        </div>

        {/* Connected Accounts Selection */}
        {formData?.brandId && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Select Connected Accounts *
            </label>
            {loadingAccounts ? (
              <div className="text-center py-4 text-gray-500">Loading accounts...</div>
            ) : connectedAccounts?.length === 0 ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">No connected accounts</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Please connect social media accounts to this brand in Brand Management before creating posts.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {connectedAccounts?.map(account => {
                  const platformInfo = getPlatformLogo(account?.platform);
                  return (
                    <div
                      key={account?.id}
                      onClick={() => handleAccountToggle(account?.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData?.socialAccountIds?.includes(account?.id)
                          ? 'border-primary bg-primary/5' :'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <Checkbox
                        checked={formData?.socialAccountIds?.includes(account?.id)}
                        onChange={() => {}}
                      />
                      <div className="w-6 h-6 flex items-center justify-center">
                        <Image
                          src={platformInfo?.logo}
                          alt={platformInfo?.alt}
                          className="w-full h-full object-contain"
                          style={{ filter: `drop-shadow(0 0 2px ${platformInfo?.color}40)` }}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{account?.accountName}</p>
                        <p className="text-xs text-gray-500">{platformInfo?.name}</p>
                      </div>
                      {account?.followerCount > 0 && (
                        <span className="text-xs text-gray-500">
                          {account?.followerCount?.toLocaleString()} followers
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {errors?.socialAccountIds && (
              <p className="text-sm text-error mt-2">{errors?.socialAccountIds}</p>
            )}
          </div>
        )}

        {/* AI Generator Toggle */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">
              Use AI to generate post content
            </span>
          </div>
          <Button
            onClick={() => setShowAIGenerator(!showAIGenerator)}
            variant="outline"
            size="sm"
          >
            {showAIGenerator ? 'Hide AI Generator' : 'Open AI Generator'}
          </Button>
        </div>

        {/* AI Generator */}
        {showAIGenerator && (
          <AIPostGenerator onPostGenerated={handleAIPostGenerated} />
        )}

        {/* Content Enhancement Panel */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">
              Get AI suggestions for tone, hashtags & formatting
            </span>
          </div>
          <Button
            onClick={() => setShowEnhancement(!showEnhancement)}
            variant="outline"
            size="sm"
          >
            {showEnhancement ? 'Hide Suggestions' : 'Enhance Content'}
          </Button>
        </div>

        {showEnhancement && (
          <ContentEnhancementPanel
            content={formData?.content}
            platforms={formData?.socialAccountIds?.map(id => {
              const account = connectedAccounts?.find(acc => acc?.id === id);
              return account?.platform;
            })}
            onApplySuggestion={handleApplySuggestion}
          />
        )}

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Caption *
          </label>
          <textarea
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="Write your post caption..."
            value={formData?.content}
            onChange={(e) => setFormData(prev => ({ ...prev, content: e?.target?.value }))}
          />
          <div className="mt-1 flex justify-between items-center">
            <span className="text-xs text-gray-500">
              {current} characters{limit ? ` / ${limit} max` : ''}
            </span>
            {limit && current > limit && (
              <span className="text-xs text-error font-medium">Exceeds limit!</span>
            )}
          </div>
          {errors?.content && (
            <p className="text-sm text-error mt-2">{errors?.content}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Trending Hashtags
          </label>
          <div className="flex flex-wrap gap-2">
            {trendingHashtags?.map(hashtag => (
              <button
                key={hashtag}
                type="button"
                onClick={() => handleHashtagClick(hashtag)}
                className="px-3 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium hover:bg-accent/20 transition-colors"
              >
                {hashtag}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Media Upload
          </label>
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors">
            {mediaPreview ? (
              <div className="relative">
                <div className="w-full h-48 rounded-lg overflow-hidden mb-3">
                  <Image
                    src={mediaPreview}
                    alt={formData?.mediaAlt || "Uploaded media preview for social media post"}
                    className="w-full h-full object-cover"
                  />
                </div>
                <Input
                  type="text"
                  label="Image Description (Alt Text)"
                  placeholder="Describe the image for accessibility..."
                  value={formData?.mediaAlt}
                  onChange={(e) => setFormData(prev => ({ ...prev, mediaAlt: e?.target?.value }))}
                  className="mb-3"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setMediaPreview(null);
                    setFormData(prev => ({ ...prev, media: null, mediaAlt: '' }));
                  }}
                  iconName="X"
                >
                  Remove Media
                </Button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleMediaUpload}
                  className="hidden"
                />
                <Icon name="Upload" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-foreground font-medium mb-1">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG, GIF, MP4 up to 10MB
                </p>
              </label>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            type="date"
            label="Schedule Date"
            value={formData?.scheduledDate?.toISOString()?.split('T')?.[0]}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              scheduledDate: new Date(e.target.value) 
            }))}
          />
          <Input
            type="time"
            label="Schedule Time"
            value={formData?.scheduledTime}
            onChange={(e) => setFormData(prev => ({ ...prev, scheduledTime: e?.target?.value }))}
          />
        </div>

        <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
          <Checkbox
            checked={formData?.recurring}
            onChange={(e) => setFormData(prev => ({ ...prev, recurring: e?.target?.checked }))}
            label="Recurring Post"
          />
          {formData?.recurring && (
            <select
              value={formData?.recurringPattern}
              onChange={(e) => setFormData(prev => ({ ...prev, recurringPattern: e?.target?.value }))}
              className="px-3 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            onClick={handleSubmit}
            variant="default"
            fullWidth
            iconName="Calendar"
            iconPosition="left"
          >
            Schedule Post
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setFormData({
                brandId: '',
                content: '',
                socialAccountIds: [],
                media: null,
                mediaAlt: '',
                scheduledDate: new Date(),
                scheduledTime: '12:00',
                recurring: false,
                recurringPattern: 'daily'
              });
              setMediaPreview(null);
              setErrors({});
            }}
            iconName="RotateCcw"
          >
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PostCreationForm;